const cachedFiltersResult = new Map();
class CollectionFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.selectors = { section: '.facest-filters-section', productGridContainer: '#CollectionProductGrid', filtersForm: '#CollectionFiltersForm', sortingInToolbar: '[data-toolbar-sorting] select', sortingInForm: '[data-form-sorting] select', filtersWrapper: '.m-filter--wrapper', sidebar: '.m-sidebar', sidebarContent: '.m-sidebar--content', openSidebar: '.m-sidebar--open' };
    this.revision = 0;
    this.listeners = [];
  }
  connectedCallback() {
    this.refreshNodes();
    this.domNodes.filtersForm.addEventListener('input', this.onInput);
    this.domNodes.filtersForm.addEventListener('submit', this.onSubmitHandler);
    this.domNodes.section.addEventListener('click', this.onSectionClick);
    this.domNodes.section.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('popstate', this.onHistoryChange);
    this.initSorting();
  }
  disconnectedCallback() {
    clearTimeout(this.submitTimer);
    cancelAnimationFrame(this.restoreFrame);
    this.controller?.abort();
    this.revision++;
    this.domNodes.filtersForm.removeEventListener('input', this.onInput);
    this.domNodes.filtersForm.removeEventListener('submit', this.onSubmitHandler);
    this.domNodes.section.removeEventListener('click', this.onSectionClick);
    this.domNodes.section.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('popstate', this.onHistoryChange);
    this.listeners.forEach((off) => off());
  }
  refreshNodes() {
    this.domNodes = queryDomNodes(this.selectors);
    this.sectionId = this.domNodes.section.dataset.sectionId;
    this.view = this.domNodes.section.dataset.view;
    if (this.view !== 'search') return;
    const input = this.domNodes.section.querySelector('.m-search--form input[name="q"]');
    if (!input) return;
    const hidden = this.domNodes.filtersForm.querySelector('input[name="q"]');
    if (hidden) hidden.value = input.value;
    const clearLink = this.domNodes.section.querySelector('.m-active-facets--remove-all');
    if (clearLink) {
      const url = new URL(clearLink.href);
      url.searchParams.set('q', input.value);
      clearLink.href = url.pathname + url.search;
    }
  }
  initSorting() {
    this.listeners.forEach((off) => off());
    this.listeners = [];
    const select = this.domNodes.sortingInToolbar;
    if (select) {
      select.selectedIndex = Number(select.querySelector('option[selected]')?.dataset.index || 0);
      const change = () => this.handleSorting(select.selectedIndex);
      select.addEventListener('change', change);
      this.listeners.push(() => select.removeEventListener('change', change));
    }
    this.listeners.push(addEventDelegate({ selector: '.m-sortby-mobile--content li', handler: (event, option) => {
      MinimogTheme.Collection.closeMobileSorting();
      this.handleSorting(Number(option.dataset.index));
    }}));
  }
  handleSorting = (index) => {
    if (!this.domNodes.sortingInForm) return;
    this.domNodes.sortingInForm.selectedIndex = Number(index) || 0;
    this.onSubmitHandler();
  };
  onInput = () => {
    clearTimeout(this.submitTimer);
    this.controller?.abort();
    this.revision++;
    this.setBusy(true);
    this.submitTimer = setTimeout(() => this.onSubmitHandler(), 350);
  };
  onSubmitHandler = (event) => {
    event?.preventDefault();
    this.refreshNodes();
    this.renderPage(new URLSearchParams(new FormData(this.domNodes.filtersForm)).toString());
  };
  onHistoryChange = () => this.renderPage(new URLSearchParams(window.location.search).toString(), false);
  onKeyDown = (event) => {
    if (event.key === 'Escape' && this.domNodes.section.classList.contains('sidebar-open')) {
      MinimogTheme.Collection.closeSidebarFilter();
      this.domNodes.openSidebar?.focus({ preventScroll: true });
    }
  };
  onSectionClick = (event) => {
    const toggle = event.target.closest('.m-accordion--item-button');
    if (toggle) requestAnimationFrame(() => toggle.setAttribute('aria-expanded', String(toggle.closest('.m-accordion--item').classList.contains('open'))));
    if (event.target.closest('[data-onoff-show-results]')) {
      event.preventDefault();
      MinimogTheme.Collection.closeSidebarFilter();
      this.domNodes.openSidebar?.focus({ preventScroll: true });
      return;
    }
    if (event.target.closest('[data-onoff-filter-retry]')) {
      event.preventDefault(); this.onSubmitHandler(); return;
    }
    const shape = event.target.closest('.onoff-racket-finder a');
    if (shape && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      const input = [...this.domNodes.filtersForm.querySelectorAll('input[name="filter.p.m.custom.rackets_head_shape"]')].find((item) => item.value === shape.dataset.shapeValue);
      if (input) {
        input.checked = !input.checked;
        if (input.checked) shape.setAttribute('aria-current', 'true');
        else shape.removeAttribute('aria-current');
        this.onSubmitHandler();
      } else this.renderPage(new URL(shape.href).searchParams.toString());
      return;
    }
    const category = event.target.closest('.onoff-listing-categories a');
    if (!category || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.refreshNodes();
    const params = new URLSearchParams(new FormData(this.domNodes.filtersForm));
    const value = category.dataset.categoryValue;
    params.delete('filter.p.product_type'); params.delete('page');
    if (value) params.set('filter.p.product_type', value);
    // Preserve cross-category criteria when changing the product category.
    const cleared = [];
    for (const key of [...params.keys()]) {
      if (key.startsWith('filter.') && !['filter.p.product_type', 'filter.p.m.custom.brand', 'filter.p.vendor', 'filter.v.price.gte', 'filter.v.price.lte', 'filter.v.availability'].includes(key)) {
        const group = [...this.domNodes.filtersForm.querySelectorAll('[data-filter-param]')].find((item) => item.dataset.filterParam === key);
        const label = group?.querySelector('.m-filter--widget-title')?.textContent.trim();
        if (label && !cleared.includes(label)) cleared.push(label);
        params.delete(key);
      }
    }
    this.categoryNotice = cleared.length ? 'Category updated. Cleared: ' + cleared.join(', ') + '.' : '';
    this.renderPage(params.toString());
  };
  setBusy(busy, failed = false) {
    this.domNodes.productGridContainer?.setAttribute('aria-busy', String(busy));
    this.domNodes.section.classList.toggle('onoff-filters-loading', busy);
    this.querySelectorAll('[data-onoff-show-results]').forEach((button) => { button.disabled = busy || failed; });
    const error = this.querySelector('[data-onoff-filter-error]');
    if (error) error.hidden = !failed;
    const status = this.querySelector('[data-onoff-filter-updating]');
    if (status) status.hidden = !busy;
  }
  renderPage = async (searchParams, updateURLHash = true) => {
    clearTimeout(this.submitTimer);
    this.controller?.abort();
    const revision = ++this.revision;
    const categoryNotice = this.categoryNotice || '';
    this.categoryNotice = '';
    this.controller = new AbortController();
    this.setBusy(true);
    const previewView = new URLSearchParams(window.location.search).get('view');
    if (previewView === 'onoff-new-arrivals') {
      const previewParams = new URLSearchParams(searchParams);
      previewParams.set('view', previewView);
      searchParams = previewParams.toString();
    }
    const params = new URLSearchParams(searchParams);
    params.set('section_id', this.sectionId);
    const url = window.location.pathname + '?' + params.toString();
    try {
      let html = cachedFiltersResult.get(url);
      if (!html) {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Filter request failed');
        html = await response.text();
      }
      if (revision !== this.revision || !this.isConnected) return;
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.facest-filters-section');
      if (!next?.querySelector('#CollectionProductGrid') || !next.querySelector('#CollectionFiltersForm')) throw new Error('Incomplete filter response');
      this.renderProductGrid(next);
      const notice = this.domNodes.section.querySelector('.onoff-category-notice');
      if (notice && categoryNotice) { notice.textContent = categoryNotice; notice.hidden = false; }
      cachedFiltersResult.set(url, html);
      if (cachedFiltersResult.size > 20) cachedFiltersResult.delete(cachedFiltersResult.keys().next().value);
      if (updateURLHash) {
        const destination = window.location.pathname + (searchParams ? '?' + searchParams : '');
        if (destination !== window.location.pathname + window.location.search) history.pushState({ searchParams }, '', destination);
      }
      this.setBusy(false);
      document.dispatchEvent(new CustomEvent('collection:rerendered'));
    } catch (error) {
      if (revision !== this.revision || error.name === 'AbortError') return;
      this.setBusy(false, true);
      console.error('Unable to update filters', error);
    }
  };
  renderProductGrid(next) {
    const { filtersForm, sidebarContent, section } = this.domNodes;
    const scrollTop = sidebarContent?.scrollTop || 0;
    const pageX = window.scrollX; const pageY = window.scrollY;
    const active = document.activeElement;
    const focusedShape = active?.closest('.onoff-racket-finder a')?.dataset.shapeValue;
    const focus = filtersForm.contains(active) ? { name: active.name, value: active.value, id: active.id } : null;
    const states = new Map([...filtersForm.querySelectorAll('.m-accordion--item')].map((group) => [group.dataset.filterParam || group.dataset.index, group.classList.contains('open')]));
    const nextForm = next.querySelector('#CollectionFiltersForm');
    nextForm.querySelectorAll('.m-accordion--item').forEach((group) => {
      const key = group.dataset.filterParam || group.dataset.index;
      if (states.has(key)) group.classList.toggle('open', states.get(key));
      group.querySelector('.m-accordion--item-button')?.setAttribute('aria-expanded', String(group.classList.contains('open')));
    });
    // Keep the drawer and custom element mounted; replace only their server-rendered contents.
    filtersForm.replaceChildren(...nextForm.childNodes);
    // Category navigation lives above the heading; refresh its selected state with the grid.
    const oldCategories = section.querySelector('.onoff-listing-categories');
    const nextCategories = next.querySelector('.onoff-listing-categories');
    if (oldCategories && nextCategories && !this.domNodes.productGridContainer.contains(oldCategories)) {
      const categoryScroll = oldCategories.scrollLeft;
      oldCategories.replaceWith(nextCategories);
      nextCategories.scrollLeft = categoryScroll;
    }
    this.domNodes.productGridContainer.replaceWith(next.querySelector('#CollectionProductGrid'));
    const oldHeader = section.querySelector('.m-page-header');
    const newHeader = next.querySelector('.m-page-header');
    if (oldHeader && newHeader) {
      oldHeader.querySelector('h1').textContent = newHeader.querySelector('h1').textContent;
      oldHeader.querySelector('input[name="q"]').value = newHeader.querySelector('input[name="q"]').value;
    }
    this.refreshNodes(); this.initSorting();
    handleBackgroundImageLazyload();
    MinimogTheme.Collection.init();
    const sortingIndex = this.domNodes.sortingInForm?.selectedIndex;
    document.querySelectorAll('.m-sortby-mobile--content li').forEach((option) => option.classList.toggle('active', Number(option.dataset.index) === sortingIndex));
    cancelAnimationFrame(this.restoreFrame);
    this.restoreFrame = requestAnimationFrame(() => {
      if (sidebarContent) sidebarContent.scrollTop = scrollTop;
      if (focus) {
        const target = [...filtersForm.querySelectorAll('input, select, button')].find((input) => focus.name ? input.name === focus.name && (input.type !== 'checkbox' || input.value === focus.value) : input.id === focus.id);
        target?.focus({ preventScroll: true });
      }
      if (focusedShape) [...section.querySelectorAll('.onoff-racket-finder a')].find((link) => link.dataset.shapeValue === focusedShape)?.focus({ preventScroll: true });
      window.scrollTo({ left: pageX, top: pageY, behavior: 'instant' });
    });
  }
}
customElements.define('collection-filters-form', CollectionFiltersForm);
